import './Notifications.scss'

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Row, Col, Container, Form, Spinner } from 'react-bootstrap'

import moment from 'moment'
import cn from 'classnames'

import AccountMenu from 'components/common/navigation/AccountMenu'
import {
  scheduleNotificationsWithPayload,
  stopScheduledNotifications
} from 'components/common/helpers/Mobile'
import {
  getUserPreferenceOptions,
  getUserPreferences,
  saveUserPreferences
} from 'components/common/api/Users'
import { ROUTE_NOTIFICATIONS } from 'components/common/helpers/Paths'
import { notificationMessages } from 'components/common/api/constants'
import {
  EVENT_ACTION_CANCEL,
  EVENT_ACTION_PAGEVIEW,
  EVENT_ACTION_START,
  EVENT_CATEGORY_REMINDERS,
  saveUserEvent
} from 'components/common/api/UserEvents'
import { getDailyAffirmationsList } from '../../common/api/Practices'
import {
  KEY_PRACTICE_AFFIRMATIONS,
  KEY_PRACTICE_JOURNEYS
} from 'components/common/api'

// Define our days array
const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
]

/**
 * Practice Notifications component. Used to manage user notification preferences for specific practices
 */
export default function PracticeNotifications({
  setHeaderTitle,
  setReturnPath,
  setShowToast,
  setToastMessage
}) {
  const { practiceId } = useParams()
  const isAffirmation = practiceId === KEY_PRACTICE_AFFIRMATIONS
  const isJourney = practiceId === KEY_PRACTICE_JOURNEYS

  // Get stored user notification preferences
  const [isLoaded, setIsLoaded] = useState(false)
  const [practice, setPractice] = useState({})
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)
  const [userPreferences, setUserPreferences] = useState()
  const [dailyAffirmations, setDailyAffirmations] = useState([])

  // At what time should the user be notified?
  const defaultTime = moment().add(5, 'minutes').format('HH:mm')
  const [notificationTime, setNotificationTime] = useState(defaultTime)

  // Which days of the week the user wants to be notified - default to none
  const [daysSelected, setDaysSelected] = useState([])

  useEffect(() => {
    // If we're not on the affirmations page, don't load the affirmations
    if (!isAffirmation) return
    ;(async function getDailyAffirmationsListAsync() {
      try {
        let affirmationsTitle = await getDailyAffirmationsList()
        setDailyAffirmations(affirmationsTitle)
      } catch (e) {
        console.error(e)
      }
    })()
  }, [isAffirmation])

  // Utility function to clear out all existing notifications for this practice
  const clearExistingNotifications = () => {
    try {
      const currentSettings = userPreferences?.notifications[practiceId]
      // Return if there are no existing notifications
      if (!currentSettings) return

      // Is this a daily affirmation or a journey?
      if (isAffirmation || isJourney) {
        // Cancel all existing notifications
        stopScheduledNotifications(JSON.stringify({ key: `${practiceId}` }))
      } else {
        // Loop through each day and cancel the existing notifications
        const dayString = currentSettings.split('|')[1]
        const splitDays = dayString.includes(',')
          ? dayString.split(',')
          : [Number(dayString)]

        const currentDays = currentSettings ? [...splitDays] : []

        // Clear out any existing notifications for this practice
        currentDays.forEach(day =>
          stopScheduledNotifications(
            JSON.stringify({ id: `${practiceId}-${day}`, days: [day] })
          )
        )
      }
    } catch (e) {
      console.error(e)
    }
  }

  // Utility function to get a random body and title for the notification from the constants file
  // depending on the day number and how many days are selected
  const generateAndScheduleNotificationPayloads = () => {
    // IMPORTANT NOTE ON LOCAL NOTIFICATIONS:
    // An app can have only 64 scheduled notifications;
    // the system keeps the soonest-firing 64 notifications (with automatically rescheduled  notifications counting as a single notification) and discards the rest.
    // See https://developer.apple.com/documentation/uikit/uilocalnotification for more info

    // Prepare payload for event bridge
    const [hour, minute] = notificationTime.split(':')
    const payloadMessageTitle = 'Today’s Affirmation'
    let messages = [...notificationMessages[practiceId]]
    let notificationPayload

    // Is this a daily affirmation or a journey?
    if (isJourney) {
      // Do nothing for now, in a follow-up we'll add a custom message for journeys
    } else if (isAffirmation) {
      // Limit to 30 notifications, since we can only schedule 64 total
      // and we need to account for the other notifications the user may have scheduled
      const notificationLimit = 30

      // Shuffle the messages so we don't always get the same ones
      const affirmations = dailyAffirmations
        .sort(() => 0.5 - Math.random())
        .slice(0, notificationLimit)

      let messageArray = []

      const len = affirmations.length + 1
      for (let i = 0; i < len; i++) {
        // Replace slashes with empty string to avoid issues with the event bridge
        const affirmation = affirmations[i]?.replace(/\//g, '')

        messageArray.push({
          title: payloadMessageTitle,
          body:
            len - 1 === i // if the last element
              ? 'To keep receiving daily affirmations, open the Remble app and reset your affirmations reminders.'
              : affirmation,
          time: notificationTime
        })
      }

      // Create the payload for affirmation
      notificationPayload = {
        key: KEY_PRACTICE_AFFIRMATIONS,
        messages: messageArray
      }

      // Schedule the notification array
      scheduleNotificationsWithPayload(notificationPayload)
    } else {
      daysSelected.forEach(day => {
        // Destructure the message array
        const { title, body } = messages.splice(
          messages[Math.floor(Math.random() * messages.length)],
          1
        )[0]

        // Create the payload
        notificationPayload = {
          id: `${practiceId}-${day}`,
          days: [day],
          hour: Number(hour),
          minute: Number(minute),
          title,
          body
        }

        // Schedule the notification for each day
        scheduleNotificationsWithPayload(notificationPayload)
      })
    }
  }

  /**
   * Event handler for when the user disables notifications for this practice
   */
  const handleDisableNotifications = () => {
    // Disable notifications
    const updatedPreferences = {
      ...userPreferences,
      notifications: {
        ...userPreferences?.notifications,
        [practiceId]: '' // Set the practice's notification time to an empty string
      }
    }

    // Clear out any existing notifications for this practice
    clearExistingNotifications()

    // Save the user's preferences
    saveUserPreferences(updatedPreferences)

    // Log the event
    saveUserEvent(EVENT_CATEGORY_REMINDERS, EVENT_ACTION_CANCEL, practiceId)
  }

  /**
   * Handler function to save the user's notification preferences for a specific practice
   * Generates a payload to send to the event bridge to schedule notifications on the user's device
   */
  const handleSave = () => {
    // Prepare payload for event bridge
    const [hour, minute] = notificationTime.split(':')

    // Convert days and times to comma, colon and pipe separated strings
    const daysString = daysSelected.sort().join(',')
    const timeString = `${hour}:${minute}`

    const timeAndDayString = isAffirmation
      ? timeString
      : `${timeString}|${daysString}`

    const updatedPreferences = {
      ...userPreferences,
      notifications: {
        ...userPreferences?.notifications,
        [practiceId]: timeAndDayString
      }
    }

    // Save the user's preferences
    saveUserPreferences(updatedPreferences)

    // Clear out any existing notifications for this practice
    clearExistingNotifications()

    // Generate new notification payloads
    generateAndScheduleNotificationPayloads()

    // Log the event
    saveUserEvent(EVENT_CATEGORY_REMINDERS, EVENT_ACTION_START, practiceId)

    // Show success toast
    setToastMessage(`${practice?.title} reminders updated`)
    setShowToast(true)
  }

  useEffect(() => {
    // Return early if we don't have a practiceId
    if (!practiceId) return

    // Scroll to top
    window.scrollTo(0, 0)

    // Set the header title
    setHeaderTitle('Reminders')

    // Set the return path
    setReturnPath(ROUTE_NOTIFICATIONS)

    // Get the user's notification preferences
    async function getUserPreferencesAsync() {
      const [options, preferences] = await Promise.all([
        getUserPreferenceOptions(),
        getUserPreferences()
      ])

      const notificationOptions = options.notifications // Get the notification options
      const practiceOptions = notificationOptions.find(
        x => x.key === practiceId
      )

      const splitedPreferences =
        preferences.notifications[practiceId]?.split('|')

      // Try to split the time and days string into an array
      try {
        if (isAffirmation) {
          if (preferences.notifications[practiceId] != null) {
            const [time] = splitedPreferences
            setNotificationTime(time)
            setNotificationsEnabled(
              preferences.notifications[practiceId] !== ''
            )
          }
          setDaysSelected([1, 2, 3, 4, 5, 6, 7])
        } else {
          const [time, days] = splitedPreferences
          const daysSorted = days?.includes(',')
            ? days
                .split(',')
                .map(day => Number(day))
                .sort()
            : [Number(days)]

          // All set, update state
          setNotificationTime(time)
          setDaysSelected(daysSorted)
          setNotificationsEnabled(true)
        }
      } catch (e) {
        // If the user doesn't have any preferences set, don't do anything
        console.error(e)
      }

      // Set practice options for the user to select
      setPractice(practiceOptions)
      setUserPreferences(preferences)
      setIsLoaded(true)

      // Log access to this page
      saveUserEvent(EVENT_CATEGORY_REMINDERS, EVENT_ACTION_PAGEVIEW, practiceId)
    }
    getUserPreferencesAsync()
  }, [practiceId, setHeaderTitle, setReturnPath, isAffirmation])

  const handleInputChange = weekdayIndex => {
    // If the day is already selected, remove it
    if (daysSelected.includes(weekdayIndex)) {
      setDaysSelected(daysSelected.filter(x => x !== weekdayIndex))
    } else {
      // Otherwise, add it
      setDaysSelected([...daysSelected, weekdayIndex])
    }
  }

  return (
    <Container className='profile-container'>
      <Row xs={1} md={2}>
        {/* Sidebar panel for larger screens */}
        <Col md={4} className='mobile-hidden account-sidebar'>
          <AccountMenu />
        </Col>
        <Col md={8}>
          <h1 className='title green'>{practice?.title}</h1>
          {!isLoaded ? (
            <>
              <Spinner
                animation='border'
                role='status'
                className='inline-spinner mx-2'
                variant='light'
                size='sm'
              />
              Loading...
            </>
          ) : (
            <>
              <Form.Group
                controlId='formEnableNotifications'
                className='form-group green-group visible mb-3'
              >
                <p>
                  Would you like to receive daily reminders for this practice
                  area on Remble?
                </p>
                <Form.Check
                  id='enableNotifications'
                  size='lg'
                  label={
                    notificationsEnabled
                      ? 'Reminders are on'
                      : 'Turn On Reminders'
                  }
                  type='switch'
                  onChange={e => {
                    setNotificationsEnabled(e.currentTarget.checked)

                    // Disable notifications for this practice
                    if (!e.currentTarget.checked) handleDisableNotifications()
                  }}
                  checked={notificationsEnabled}
                  disabled={!isLoaded}
                />
              </Form.Group>
              <div
                className={cn('form-group', { visible: notificationsEnabled })}
              >
                <h3>When would you like to be reminded?</h3>
                <>
                  <Form.Group
                    className={
                      isAffirmation
                        ? 'form-group gray-group mt-4'
                        : 'center text-center py-4'
                    }
                  >
                    {isAffirmation && (
                      <label htmlFor='startTime'>Start time</label>
                    )}

                    {isJourney && (
                      <p className='muted'>
                        <strong>NOTE:</strong> Session reminders can be updated
                        at the end of each Session.
                      </p>
                    )}

                    <input
                      type='time'
                      pattern='[0-9]{2}:[0-9]{2}'
                      id='startTime'
                      name='startTime'
                      defaultValue={notificationTime}
                      onChange={e => setNotificationTime(e.currentTarget.value)}
                    />
                  </Form.Group>
                  <Form.Group
                    className={cn('center text-center', {
                      'd-none': isAffirmation || isJourney
                    })}
                  >
                    {days.map((day, index) => {
                      // Sunday is the first day of the week and ios uses 1-7 for days
                      const weekdayIndex = index + 1
                      return (
                        <label
                          className={cn('daybox-input-wrapper', {
                            active: daysSelected.includes(weekdayIndex)
                          })}
                          key={day}
                        >
                          <input
                            type='checkbox'
                            id={day}
                            className='daybox-input'
                            onChange={() => handleInputChange(weekdayIndex)}
                            checked={daysSelected.includes(weekdayIndex)}
                          />
                          <span className='daybox-label'>{day.charAt(0)}</span>
                        </label>
                      )
                    })}
                  </Form.Group>
                </>
                <Form.Group
                  className={cn('center text-center py-4', {
                    'd-none': isJourney
                  })}
                >
                  <Button
                    variant='primary'
                    size='lg'
                    className='px-5'
                    disabled={
                      !isLoaded ||
                      !notificationsEnabled ||
                      !daysSelected.length ||
                      !notificationTime
                    }
                    onClick={() => handleSave()}
                  >
                    Save
                  </Button>
                </Form.Group>
              </div>
            </>
          )}
        </Col>
      </Row>
    </Container>
  )
}
